import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, qs } from "@/lib/api";
import { useKeys } from "@/lib/queries";
import type { TextTemplate, TextTemplateInput } from "@/lib/text-templates";

/**
 * core-api's `/message-templates` (ADR-111 in platform, ACL-385). Admins only, as the workflows
 * are: a template is what a workflow texts leads in the tenant's name. One template per slug, with
 * its English and, when written, Spanish text. Every change answers with the template as it now
 * is; the list and the workflow catalog (which offers the templates to the builder) are refetched.
 */

const own = (tenantId?: number) => tenantId ?? "own";

export function useTextTemplates(tenantId?: number, enabled = true) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.scoped(["text-templates", own(tenantId)]),
    queryFn: () => api.get<TextTemplate[]>(`/message-templates${qs({ tenant_id: tenantId })}`),
    enabled,
  });
}

/** One template. A superadmin names the tenant: a slug is unique only inside one. */
export function useTextTemplate(slug: string | undefined, tenantId?: number) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.scoped(["text-templates", "detail", own(tenantId), slug ?? ""]),
    queryFn: () =>
      api.get<TextTemplate>(`/message-templates/${encodeURIComponent(slug ?? "")}${qs({ tenant_id: tenantId })}`),
    enabled: Boolean(slug),
  });
}

/** The answer goes in the cache; the lists and the builder's catalog are read again. */
function useSettle(tenantId?: number) {
  const keys = useKeys();
  const qc = useQueryClient();
  return (saved: TextTemplate) => {
    const detail = keys.scoped(["text-templates", "detail", own(tenantId), saved.slug]);
    // A read already on its way began before this write and would land on top of it.
    void qc.cancelQueries({ queryKey: detail, exact: true });
    qc.setQueryData(detail, saved);
    const prefix = keys.scoped(["text-templates"]);
    void qc.invalidateQueries({ queryKey: prefix, predicate: (q) => q.queryKey[prefix.length] !== "detail" });
    void qc.invalidateQueries({ queryKey: keys.scoped(["workflows", "catalog"]) });
  };
}

export function useCreateTextTemplate(tenantId?: number) {
  const settle = useSettle(tenantId);
  return useMutation({
    mutationFn: (input: TextTemplateInput & { slug: string }) =>
      api.post<TextTemplate>("/message-templates", tenantId ? { ...input, tenant_id: tenantId } : input),
    onSuccess: settle,
  });
}

export function useSaveTextTemplate(tenantId?: number) {
  const settle = useSettle(tenantId);
  return useMutation({
    mutationFn: ({ slug, ...input }: TextTemplateInput & { slug: string }) =>
      api.put<TextTemplate>(`/message-templates/${encodeURIComponent(slug)}${qs({ tenant_id: tenantId })}`, input),
    onSuccess: settle,
  });
}

/** Turn a template off or back on. core-api refuses to turn off one a published workflow sends. */
export function useTextTemplateActive(tenantId?: number) {
  const settle = useSettle(tenantId);
  return useMutation({
    mutationFn: ({ slug, active }: { slug: string; active: boolean }) =>
      api.put<TextTemplate>(
        `/message-templates/${encodeURIComponent(slug)}/active${qs({ tenant_id: tenantId })}`,
        { active },
      ),
    onSuccess: settle,
  });
}
